import { Accumulator, type Advance } from "./timestep";

/*
 * The host loop: the one place in this client that reads a clock.
 *
 * `requestAnimationFrame` hands us the frame's timestamp, we hand the elapsed
 * duration to the accumulator, and the accumulator decides how many fixed steps
 * the wall clock has earned (`host/timestep.ts`). Everything below this file
 * takes time as a parameter or does not see it at all, which is the same sans-io
 * discipline the sim core is held to (rust guide §8.1) and the reason the
 * accumulator's checks can drive an hour of frames in a millisecond.
 *
 * Frame shape, in order: step the simulation zero or more times, then render
 * once with the leftover fraction. Rendering after stepping means the frame
 * shows the freshest state; interpolating by `alpha` means a 30 Hz simulation
 * does not look like a 30 Hz game.
 */

/** What the loop drives. Implemented in `main.ts` and nowhere else. */
export interface HostLoopHandlers {
  /**
   * Runs once per frame, before any step.
   *
   * Where per-frame input is consumed: a frame's steps then all see the same
   * view direction, and a frame that runs no steps still turns the camera.
   */
  beginFrame(frameSeconds: number): void;

  /**
   * Advances the world and the player by one fixed tick.
   *
   * `stepSeconds` is the constant `1 / TICK_HZ`, passed rather than imported so
   * that the tick rate stays a single constant in a single file.
   */
  step(stepSeconds: number): void;

  /**
   * Draws one frame.
   *
   * `alpha` is how far the frame sits between the last step and the next, in
   * 0..1; `frameSeconds` is the wall time since the previous frame, for things
   * that are honestly frame-rate dependent (mouse look, HUD smoothing).
   */
  render(alpha: number, frameSeconds: number): void;
}

/** Loop health, for the HUD. */
export interface HostStats {
  /** Smoothed frames per second. Zero until the second frame. */
  readonly fps: number;

  /** Frames in which the max-steps clamp engaged since the loop started. */
  readonly clampedFrames: number;

  /** Ticks that were due and were abandoned by the clamp, in total. */
  readonly droppedSteps: number;

  /** Ticks run in the most recent frame. */
  readonly stepsLastFrame: number;
}

/**
 * How strongly a new frame time moves the smoothed frame time.
 *
 * A tenth: fast enough that a real slowdown shows up within a few frames, slow
 * enough that the HUD's number is readable instead of a blur.
 */
const FPS_SMOOTHING = 0.1;

/** Drives {@link HostLoopHandlers} against the display's refresh. */
export class HostLoop {
  private readonly handlers: HostLoopHandlers;
  private readonly accumulator: Accumulator;

  /**
   * The step callback, bound once.
   *
   * Handed to the accumulator every frame; binding it here rather than there
   * keeps the hot path free of a closure allocation per frame and keeps `this`
   * attached to the handler object.
   */
  private readonly stepCallback: (stepSeconds: number) => void;

  /** The previous frame's `requestAnimationFrame` timestamp, or `null` before the first. */
  private previousTimestamp: number | null = null;

  private rafHandle: number | null = null;
  private smoothedFrameMillis = 0;
  private clampedFrames = 0;
  private droppedSteps = 0;
  private stepsLastFrame = 0;

  constructor(handlers: HostLoopHandlers, accumulator: Accumulator = new Accumulator()) {
    this.handlers = handlers;
    this.accumulator = accumulator;
    this.stepCallback = (stepSeconds: number): void => {
      handlers.step(stepSeconds);
    };
  }

  get stats(): HostStats {
    return {
      fps: this.smoothedFrameMillis > 0 ? 1000 / this.smoothedFrameMillis : 0,
      clampedFrames: this.clampedFrames,
      droppedSteps: this.droppedSteps,
      stepsLastFrame: this.stepsLastFrame,
    };
  }

  /** Starts the loop. Idempotent — a second call while running does nothing. */
  start(): void {
    if (this.rafHandle !== null) {
      return;
    }

    // `null` rather than a primed timestamp: the first frame credits zero
    // elapsed time, which keeps the clock sampled in exactly one place. Priming
    // it here would be a second call site, and a second call site is how a
    // sans-io boundary starts leaking.
    this.previousTimestamp = null;
    this.rafHandle = requestAnimationFrame(this.onFrame);
  }

  /** Stops the loop. Safe to call when it is not running. */
  stop(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  /**
   * One frame. Bound as a field so `requestAnimationFrame` keeps `this`.
   *
   * A backgrounded tab stops receiving frames entirely, so the first frame after
   * a tab switch arrives with minutes of elapsed time. That is not a special
   * case: it is exactly what the max-steps clamp is for, and the loop returns to
   * real time on the following frame having abandoned the backlog rather than
   * trying to simulate the coffee break.
   */
  private readonly onFrame = (timestamp: number): void => {
    this.rafHandle = requestAnimationFrame(this.onFrame);

    const elapsedMillis = this.previousTimestamp === null ? 0 : timestamp - this.previousTimestamp;
    this.previousTimestamp = timestamp;

    if (elapsedMillis > 0) {
      this.smoothedFrameMillis =
        this.smoothedFrameMillis === 0
          ? elapsedMillis
          : this.smoothedFrameMillis + (elapsedMillis - this.smoothedFrameMillis) * FPS_SMOOTHING;
    }

    this.handlers.beginFrame(elapsedMillis / 1000);

    const advance: Advance = this.accumulator.advance(elapsedMillis, this.stepCallback);
    this.stepsLastFrame = advance.steps;

    if (advance.droppedSteps > 0) {
      this.clampedFrames += 1;
      this.droppedSteps += advance.droppedSteps;
    }

    this.handlers.render(this.accumulator.alpha, elapsedMillis / 1000);
  };
}
